import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  StudioCanvas,
  BeatStrip,
  BeatCard,
  BeatState,
  BeatVariant,
  GenerateButton,
  Button,
  Tag,
} from "../../../../../../design-system";
import { Author } from "../../../../../../types/Author";
import { ReelCharacter } from "../../../../../../types/ReelCharacter";
import { ReelSegment } from "./types";
import {
  WorkflowStep,
  ImageProvider,
  VideoFormat,
  BusinessStoryCandidate,
  GenerationSubstep,
} from "./ViralReelCreator.types";

export type ViralReelStudioViewProps = {
  // Core state
  segments: ReelSegment[];
  promptInputs: string[];
  scriptInputs: string[];
  currentStep: WorkflowStep;
  progress: string;
  progressPercent: number;
  generationSubsteps: GenerationSubstep[];
  isGenerating: boolean;
  isSaving: boolean;
  isDownloading: boolean;
  isDownloadingVoice: boolean;
  isCombiningReel: boolean;
  combinedReelUrl: string;

  // AI generation tracking
  aiGeneratingIndex: number | null;
  aiGeneratingAll: boolean;
  aiGeneratingAllProgress: string;

  // Voice / transitions
  isGeneratingVoice: boolean;
  voiceProgress: string;
  isGeneratingTransitions: boolean;
  generatingTransitionIndex: number | null;
  transitionProgress: string;
  reelVoiceAudioUrl: string;

  // Story candidates
  storyCandidates: BusinessStoryCandidate[];
  selectedStoryCandidateIndex: number;

  // Quality scoring (per-reel 5-dimension scorecard from the validator)
  qualityScores?: Record<string, number>;
  qualityScoreStatus?: string;

  // Tool state
  videoFormat: VideoFormat;
  imageProvider: ImageProvider;
  voiceAuthors: Author[];
  selectedVoiceAuthorId: string;

  // Cast (multi-character dialogue mode)
  castCharacters: ReelCharacter[];
  selectedCastIds: string[];
  onToggleCastMember: (characterId: string) => void;

  hasContent: boolean;
  canSave: boolean;
  primaryColor?: string;

  // Handlers
  onFormatChange: (f: VideoFormat) => void;
  onProviderChange: (p: ImageProvider) => void;
  onVoiceAuthorChange: (authorId: string) => void;
  onGenerate: () => void;

  onSelectStoryCandidate: (index: number) => void;
  onContinueWithSelectedStory: () => void;
  onRefreshStories: () => void;

  onScriptChange: (idx: number, value: string) => void;
  onPromptChange: (idx: number, value: string) => void;
  onSelectOption: (segIdx: number, optIdx: number) => void;
  onGenerateAI: (idx: number) => void;
  onGenerateAll: () => void;
  onUpload: (segIdx: number, file: File | null) => void;
  onGenerateTransition: (idx: number) => void;
  onGenerateTransitions: () => void;
  onGenerateVoice: () => void;
  onCombineReel: () => void;
  onDownloadAll: () => void;
  onDownloadCombinedVoice: () => void;

  onSave: () => void;
  onClear: () => void;
  onRewriteDimension?: (segmentIndex: number, dimension: string) => void;
};

const VIDEO_FORMATS: { id: VideoFormat; label: string; icon: string }[] = [
  { id: "hard-truth", label: "Hard Truth", icon: "⚡" },
  { id: "storytelling", label: "Storytelling", icon: "🎬" },
  { id: "tutorial", label: "Tutorial", icon: "🗺️" },
  { id: "before-after", label: "Before / After", icon: "🔄" },
  { id: "day-in-life", label: "Day in Life", icon: "📓" },
  { id: "ugc-rant", label: "UGC Rant", icon: "🔥" },
  { id: "historic-arc", label: "Historic Arc", icon: "📜" },
  { id: "expert-revelation", label: "Expert Reveal", icon: "🧠" },
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
`;

const CastRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const CastChip = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 3px;
  border-radius: var(--radius-pill);
  border: 1.5px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) =>
    p.$active ? "var(--color-accent-subtle)" : "var(--color-surface)"};
  color: ${(p) =>
    p.$active ? "var(--color-accent-text)" : "var(--color-text-secondary)"};
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)"};
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CastAvatar = styled.span<{ $src?: string }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) =>
    p.$src
      ? `url(${p.$src}) center / cover no-repeat`
      : "var(--color-surface-subtle)"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const CastHint = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-tertiary);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
`;

const ToolGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ToolLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ToolSelect = styled.select`
  appearance: none;
  height: 36px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Spacer = styled.div`
  flex: 1;
  min-width: var(--space-3);
`;

const CandidateRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-3);
`;

const CandidateCard = styled.button<{ $active: boolean }>`
  appearance: none;
  text-align: left;
  background: var(--color-surface);
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  box-shadow: ${(p) =>
    p.$active ? "0 0 0 3px var(--color-focus-ring)" : "var(--shadow-xs)"};
  border-radius: var(--radius-lg);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  cursor: pointer;
  transition:
    border-color var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const CandidateHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const CandidateName = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CandidateMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
`;

const CandidateField = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: var(--line-height-base);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CandidateFieldLabel = styled.span`
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-size: 9px;
  display: block;
  margin-bottom: 2px;
`;

const SectionTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const SectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
`;

const DetailHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
`;

const DetailTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TextAreaField = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 64px;
  resize: vertical;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
    border-color: var(--color-border-strong);
  }
`;

const QualityChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  width: 100%;
`;

type ChipTone = "strong" | "ok" | "weak" | "muted";

const chipBg: Record<ChipTone, string> = {
  strong: "var(--color-success-subtle)",
  ok: "var(--color-warning-subtle)",
  weak: "var(--color-danger-subtle)",
  muted: "var(--color-surface-subtle)",
};
const chipFg: Record<ChipTone, string> = {
  strong: "var(--color-success)",
  ok: "var(--color-warning)",
  weak: "var(--color-danger)",
  muted: "var(--color-text-tertiary)",
};
const chipBorder: Record<ChipTone, string> = {
  strong: "var(--color-success)",
  ok: "var(--color-warning)",
  weak: "var(--color-danger)",
  muted: "var(--color-border-subtle)",
};

const QualityChipButton = styled.button<{ $tone: ChipTone; $clickable: boolean }>`
  appearance: none;
  flex: 1 0 auto;
  min-width: 0;
  padding: 2px 6px;
  border-radius: var(--radius-pill);
  border: 1px solid ${(p) => chipBorder[p.$tone]};
  background: ${(p) => chipBg[p.$tone]};
  color: ${(p) => chipFg[p.$tone]};
  font-family: var(--font-sans);
  font-size: 9px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  line-height: 1.2;

  &:hover {
    filter: ${(p) => (p.$clickable ? "brightness(1.03)" : "none")};
  }
`;

const QualityChipLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QualityChipScore = styled.span`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  opacity: 0.85;
`;

const QUALITY_DIMENSIONS: { key: string; label: string }[] = [
  { key: "hook", label: "Hook" },
  { key: "pain", label: "Pain" },
  { key: "move", label: "Move" },
  { key: "reframe", label: "Reframe" },
  { key: "lesson", label: "Lesson" },
];

const scoreToTone = (score: number | undefined): ChipTone => {
  if (typeof score !== "number" || Number.isNaN(score)) return "muted";
  if (score >= 7.5) return "strong";
  if (score >= 5) return "ok";
  return "weak";
};

const formatScore = (score: number | undefined): string => {
  if (typeof score !== "number" || Number.isNaN(score)) return "—";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
};

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  flex-wrap: wrap;
`;

const FooterStats = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const PreviewArea = styled.div<{ $primary: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: #000;
`;

const PreviewMedia = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: var(--space-6);
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.78) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0) 75%
  );
  pointer-events: none;
`;

const PreviewScript = styled.div`
  font-family: var(--font-sans);
  font-size: clamp(14px, 2.4vw, 18px);
  font-weight: var(--font-weight-medium);
  color: #fff;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
  max-width: 32ch;
`;

const PreviewIndex = styled.div`
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
`;

const segmentState = (
  segment: ReelSegment | undefined,
  generatingThis: boolean
): BeatState => {
  if (generatingThis) return "generating";
  if (!segment) return "empty";
  if (segment.options && segment.options.length > 0) return "ready";
  return "queued";
};

const buildSegmentVariants = (segment: ReelSegment): BeatVariant[] => {
  const variants: BeatVariant[] = (segment.options || []).map((opt, i) => ({
    id: `opt-${i}`,
    kind: "image",
    src: opt.thumbnailUrl || opt.imageUrl,
  }));
  if (segment.transitionVideoUrl) {
    variants.unshift({
      id: "transition",
      kind: "video",
      src: segment.transitionVideoUrl,
      posterSrc: segment.options?.[segment.selectedIndex || 0]?.imageUrl,
    });
  }
  return variants;
};

export const ViralReelStudioView: React.FC<ViralReelStudioViewProps> = (props) => {
  const {
    segments,
    promptInputs,
    scriptInputs,
    currentStep,
    progress,
    progressPercent,
    isGenerating,
    isSaving,
    isDownloading,
    isDownloadingVoice,
    isCombiningReel,
    combinedReelUrl,
    aiGeneratingIndex,
    aiGeneratingAll,
    aiGeneratingAllProgress,
    isGeneratingVoice,
    voiceProgress,
    isGeneratingTransitions,
    generatingTransitionIndex,
    transitionProgress,
    reelVoiceAudioUrl,
    storyCandidates,
    selectedStoryCandidateIndex,
    qualityScores,
    qualityScoreStatus,
    onRewriteDimension,
    videoFormat,
    imageProvider,
    voiceAuthors,
    selectedVoiceAuthorId,
    castCharacters,
    selectedCastIds,
    onToggleCastMember,
    hasContent,
    canSave,
    primaryColor,
    onFormatChange,
    onProviderChange,
    onVoiceAuthorChange,
    onGenerate,
    onSelectStoryCandidate,
    onContinueWithSelectedStory,
    onRefreshStories,
    onScriptChange,
    onPromptChange,
    onSelectOption,
    onGenerateAI,
    onGenerateAll,
    onUpload,
    onGenerateTransition,
    onGenerateTransitions,
    onGenerateVoice,
    onCombineReel,
    onDownloadAll,
    onDownloadCombinedVoice,
    onSave,
    onClear,
  } = props;

  const [activeIdx, setActiveIdx] = useState<number>(0);
  const safeActiveIdx = Math.min(activeIdx, Math.max(0, segments.length - 1));
  const activeSegment = segments[safeActiveIdx];
  const activeOption = activeSegment?.options?.[activeSegment.selectedIndex];
  const activeScript = scriptInputs[safeActiveIdx] ?? activeSegment?.script ?? "";
  const activePrompt = promptInputs[safeActiveIdx] ?? activeSegment?.imagePrompt ?? "";

  const isBusy =
    isGenerating ||
    isSaving ||
    isGeneratingVoice ||
    isGeneratingTransitions ||
    isCombiningReel ||
    isDownloading;

  const showStoryCandidates =
    currentStep === "story-selecting" && storyCandidates.length > 0 && !isGenerating;

  const canvasState = useMemo(() => {
    if (isGenerating || currentStep === "generating") return "generating";
    if (combinedReelUrl) return "ready";
    if (segments.length === 0) return "empty";
    if (activeSegment && !activeOption?.imageUrl && !activeSegment.transitionVideoUrl)
      return "empty";
    return "ready";
  }, [isGenerating, currentStep, segments.length, activeSegment, activeOption, combinedReelUrl]);

  const progressLabel = isGeneratingVoice
    ? voiceProgress
    : isGeneratingTransitions
    ? transitionProgress
    : aiGeneratingAll
    ? aiGeneratingAllProgress
    : progress;

  const segmentDurationMs = 5000;
  const totalDurationMs = segments.length * segmentDurationMs;

  return (
    <Container>
      <Toolbar>
        <ToolGroup>
          <ToolLabel>Video format</ToolLabel>
          <ToolSelect
            value={videoFormat}
            onChange={(e) => onFormatChange(e.target.value as VideoFormat)}
            disabled={isBusy}
          >
            {VIDEO_FORMATS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.icon} {f.label}
              </option>
            ))}
          </ToolSelect>
        </ToolGroup>

        <ToolGroup>
          <ToolLabel>Image model</ToolLabel>
          <ToolSelect
            value={imageProvider}
            onChange={(e) => onProviderChange(e.target.value as ImageProvider)}
            disabled={isBusy}
          >
            <option value="gemini">Gemini</option>
            <option value="gpt">GPT Image</option>
          </ToolSelect>
        </ToolGroup>

        <ToolGroup>
          <ToolLabel>Voice author</ToolLabel>
          <ToolSelect
            value={selectedVoiceAuthorId}
            onChange={(e) => onVoiceAuthorChange(e.target.value)}
            disabled={isBusy || voiceAuthors.length === 0}
          >
            <option value="">No voice</option>
            {voiceAuthors.map((author) => (
              <option key={author.id} value={author.id}>
                {author.fullName}
              </option>
            ))}
          </ToolSelect>
        </ToolGroup>

        {castCharacters.length > 0 && (
          <ToolGroup>
            <ToolLabel>Cast</ToolLabel>
            <CastRow>
              {castCharacters.map((c) => {
                const active = selectedCastIds.includes(c.id);
                return (
                  <CastChip
                    key={c.id}
                    type="button"
                    $active={active}
                    onClick={() => onToggleCastMember(c.id)}
                    disabled={isBusy}
                    title={c.visualDescription}
                  >
                    <CastAvatar $src={c.referenceImageUrl}>
                      {!c.referenceImageUrl && "🎭"}
                    </CastAvatar>
                    {c.name}
                  </CastChip>
                );
              })}
              <CastHint>
                {selectedCastIds.length >= 2
                  ? `Dialogue mode: ${selectedCastIds.length} characters speak their own lines.`
                  : "Pick 2+ characters to switch to full-dialogue mode."}
              </CastHint>
            </CastRow>
          </ToolGroup>
        )}

        <Spacer />

        <GenerateButton
          label={segments.length > 0 ? "Regenerate story" : "Generate reel"}
          hint={
            !hasContent ? "Add a step script first" : "Story → script → segments"
          }
          loading={isGenerating}
          disabled={!hasContent || isBusy}
          onClick={onGenerate}
          fullWidth={false}
        />
      </Toolbar>

      {showStoryCandidates && (
        <DetailPanel>
          <SectionRow>
            <SectionTitle>Pick a story angle</SectionTitle>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={onRefreshStories} disabled={isBusy}>
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onContinueWithSelectedStory}
                disabled={isBusy || selectedStoryCandidateIndex < 0}
              >
                Continue
              </Button>
            </div>
          </SectionRow>
          <CandidateRow>
            {storyCandidates.map((cand, i) => (
              <CandidateCard
                key={`${cand.name}-${i}`}
                type="button"
                $active={i === selectedStoryCandidateIndex}
                onClick={() => onSelectStoryCandidate(i)}
              >
                <CandidateHead>
                  <CandidateName title={cand.name}>{cand.name}</CandidateName>
                  <CandidateMeta>
                    <Tag variant="neutral" size="sm">
                      {cand.year}
                    </Tag>
                    <Tag variant="accent" size="sm">
                      {Math.round((cand.score ?? 0) * 10) / 10}
                    </Tag>
                  </CandidateMeta>
                </CandidateHead>
                <div>
                  <CandidateFieldLabel>Problem</CandidateFieldLabel>
                  <CandidateField>{cand.problem}</CandidateField>
                </div>
                <div>
                  <CandidateFieldLabel>Decisive move</CandidateFieldLabel>
                  <CandidateField>{cand.decisive_move}</CandidateField>
                </div>
                <div>
                  <CandidateFieldLabel>Likely reframe</CandidateFieldLabel>
                  <CandidateField>{cand.likely_reframe}</CandidateField>
                </div>
              </CandidateCard>
            ))}
          </CandidateRow>
        </DetailPanel>
      )}

      <StudioCanvas
        aspect="9:16"
        state={canvasState}
        progressLabel={progressLabel || undefined}
        progressPercent={
          progressPercent > 0 && (currentStep === "generating" || isGenerating)
            ? progressPercent
            : undefined
        }
        emptyTitle={segments.length === 0 ? "No reel yet" : "Pick a segment"}
        emptyHint={
          segments.length === 0
            ? "Hit Generate to draft 6 segments — script, image prompts, and Unsplash visuals."
            : "Choose a segment below to preview and edit script + image."
        }
        maxHeight="56vh"
      >
        {combinedReelUrl ? (
          <PreviewVideo
            src={combinedReelUrl}
            controls
            playsInline
            poster={activeOption?.imageUrl}
          />
        ) : activeSegment && activeSegment.transitionVideoUrl ? (
          <PreviewArea $primary={primaryColor || "#FFD700"}>
            <PreviewVideo
              src={activeSegment.transitionVideoUrl}
              autoPlay
              loop
              muted
              playsInline
            />
            <PreviewIndex>
              {String(safeActiveIdx + 1).padStart(2, "0")} /{" "}
              {String(segments.length).padStart(2, "0")}
            </PreviewIndex>
            <PreviewOverlay>
              <PreviewScript>{activeSegment.script}</PreviewScript>
            </PreviewOverlay>
          </PreviewArea>
        ) : activeSegment && activeOption?.imageUrl ? (
          <PreviewArea $primary={primaryColor || "#FFD700"}>
            <PreviewMedia src={activeOption.imageUrl} alt={activeSegment.script} />
            <PreviewIndex>
              {String(safeActiveIdx + 1).padStart(2, "0")} /{" "}
              {String(segments.length).padStart(2, "0")}
            </PreviewIndex>
            <PreviewOverlay>
              <PreviewScript>{activeSegment.script}</PreviewScript>
            </PreviewOverlay>
          </PreviewArea>
        ) : null}
      </StudioCanvas>

      {activeSegment && !combinedReelUrl && (
        <DetailPanel>
          <DetailHead>
            <DetailTitle>
              Segment {String(safeActiveIdx + 1).padStart(2, "0")} / {String(segments.length).padStart(2, "0")}
            </DetailTitle>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {activeSegment.speakerName && (
                <Tag variant="accent" size="sm">
                  🎙 {activeSegment.speakerName}
                </Tag>
              )}
              <Tag variant="neutral" size="sm">
                {activeSegment.options?.length || 0} option
                {(activeSegment.options?.length || 0) === 1 ? "" : "s"}
              </Tag>
              {activeSegment.transitionVideoUrl && (
                <Tag variant="success" size="sm">
                  Transition
                </Tag>
              )}
              {activeSegment.voiceAudioUrl && (
                <Tag variant="success" size="sm">
                  Voice
                </Tag>
              )}
              {activeOption?.isAiGenerated && (
                <Tag variant="accent" size="sm">
                  AI
                </Tag>
              )}
              {activeOption?.isUploaded && (
                <Tag variant="info" size="sm">
                  Uploaded
                </Tag>
              )}
            </div>
          </DetailHead>

          <FieldRow>
            <FieldLabel>Script · what the speaker says</FieldLabel>
            <TextAreaField
              value={activeScript}
              onChange={(e) => onScriptChange(safeActiveIdx, e.target.value)}
              placeholder="15–25 words, ~5 seconds spoken"
              disabled={isBusy}
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel>Image prompt</FieldLabel>
            <TextAreaField
              value={activePrompt}
              onChange={(e) => onPromptChange(safeActiveIdx, e.target.value)}
              placeholder="Describe what we should see in this beat"
              disabled={isBusy}
            />
          </FieldRow>

          <ActionRow>
            <Button
              variant="primary"
              size="sm"
              isLoading={aiGeneratingIndex === safeActiveIdx && !aiGeneratingAll}
              disabled={isBusy || !activePrompt.trim()}
              onClick={() => onGenerateAI(safeActiveIdx)}
            >
              ✨ Regenerate image
            </Button>
            <Button
              variant="secondary"
              size="sm"
              isLoading={generatingTransitionIndex === safeActiveIdx}
              disabled={isBusy || !activeOption?.imageUrl}
              onClick={() => onGenerateTransition(safeActiveIdx)}
            >
              🎞 Regenerate motion
            </Button>
            <FileButton>
              📤 Upload image
              <HiddenFileInput
                type="file"
                accept="image/*"
                disabled={isBusy}
                onChange={(e) => onUpload(safeActiveIdx, e.target.files?.[0] || null)}
              />
            </FileButton>
          </ActionRow>
        </DetailPanel>
      )}

      <BeatStrip
        title="Segments"
        hint={
          segments.length === 0
            ? "Nothing generated yet"
            : `${segments.length} segment${segments.length === 1 ? "" : "s"}`
        }
        totalDurationMs={segments.length > 0 ? totalDurationMs : undefined}
      >
        {segments.length === 0
          ? null
          : segments.map((seg, index) => {
              const variants = buildSegmentVariants(seg);
              const generatingThis =
                aiGeneratingIndex === index || generatingTransitionIndex === index;
              const activeVariantIndex = seg.transitionVideoUrl
                ? 0
                : seg.selectedIndex + (seg.transitionVideoUrl ? 1 : 0);

              // Each segment owns one of the five narrative dimensions
              // (the validator's hook/pain/move/reframe/lesson scorecard).
              // Last segment shares "lesson" with any trailing CTA beat.
              const dimensionForIndex =
                index < QUALITY_DIMENSIONS.length
                  ? QUALITY_DIMENSIONS[index]
                  : QUALITY_DIMENSIONS[QUALITY_DIMENSIONS.length - 1];
              const dimScore = qualityScores?.[dimensionForIndex.key];
              const dimTone = scoreToTone(dimScore);
              const canRewriteDim =
                Boolean(onRewriteDimension) &&
                !generatingThis &&
                !isGenerating;
              const dimChip = (
                <QualityChipRow>
                  <QualityChipButton
                    type="button"
                    $tone={dimTone}
                    $clickable={canRewriteDim}
                    disabled={!canRewriteDim}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canRewriteDim) return;
                      onRewriteDimension?.(index, dimensionForIndex.key);
                    }}
                    title={
                      canRewriteDim
                        ? `Click to rewrite the ${dimensionForIndex.label.toLowerCase()} for this beat`
                        : dimScore === undefined
                        ? `${dimensionForIndex.label} not yet scored`
                        : `${dimensionForIndex.label} score: ${formatScore(dimScore)}`
                    }
                  >
                    <QualityChipLabel>{dimensionForIndex.label}</QualityChipLabel>
                    <QualityChipScore>{formatScore(dimScore)}</QualityChipScore>
                  </QualityChipButton>
                </QualityChipRow>
              );

              return (
                <BeatCard
                  key={`segment-${index}`}
                  index={index}
                  caption={seg.script}
                  state={segmentState(seg, generatingThis)}
                  variants={variants}
                  activeVariantIndex={Math.min(activeVariantIndex, Math.max(0, variants.length - 1))}
                  active={index === safeActiveIdx}
                  durationMs={segmentDurationMs}
                  aspect="9:16"
                  metaSlot={dimChip}
                  onSelect={() => setActiveIdx(index)}
                  onVariantChange={(idx) => {
                    // Skip the transition slot (first variant if it exists)
                    const transitionOffset = seg.transitionVideoUrl ? 1 : 0;
                    if (idx < transitionOffset) return;
                    onSelectOption(index, idx - transitionOffset);
                  }}
                  onRegenerate={() => onGenerateAI(index)}
                />
              );
            })}
      </BeatStrip>

      <FooterBar>
        <FooterStats>
          {segments.length > 0 && (
            <>
              <Tag variant="neutral" size="sm">
                {segments.length} segment{segments.length === 1 ? "" : "s"}
              </Tag>
              <Tag
                variant={reelVoiceAudioUrl ? "success" : "neutral"}
                size="sm"
                withDot
              >
                Voice
              </Tag>
              <Tag
                variant={
                  segments.some((s) => s.transitionVideoUrl) ? "success" : "neutral"
                }
                size="sm"
                withDot
              >
                Motion
              </Tag>
              <Tag
                variant={combinedReelUrl ? "success" : "neutral"}
                size="sm"
                withDot
              >
                Combined reel
              </Tag>
            </>
          )}
        </FooterStats>
        <FooterActions>
          {segments.length > 0 && (
            <>
              <Button variant="secondary" size="sm" disabled={isBusy} onClick={onClear}>
                Clear
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={aiGeneratingAll}
                disabled={isBusy}
                onClick={onGenerateAll}
              >
                AI on all
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isGeneratingVoice}
                disabled={isBusy}
                onClick={onGenerateVoice}
              >
                🎙 Voice
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isGeneratingTransitions}
                disabled={isBusy}
                onClick={onGenerateTransitions}
              >
                🎞 Motion
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isCombiningReel}
                disabled={isBusy}
                onClick={onCombineReel}
              >
                🧩 Combine
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isDownloading}
                disabled={isBusy}
                onClick={onDownloadAll}
              >
                Images
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isDownloadingVoice}
                disabled={isBusy || !reelVoiceAudioUrl}
                onClick={onDownloadCombinedVoice}
              >
                Voice MP3
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={isSaving}
                disabled={!canSave || isBusy}
                onClick={onSave}
              >
                Save segments
              </Button>
            </>
          )}
        </FooterActions>
      </FooterBar>
    </Container>
  );
};

ViralReelStudioView.displayName = "ViralReelStudioView";

export default ViralReelStudioView;
